import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Upload, FileText, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
// Phase B — `GoogleCalendarCard` déplacé vers `SettingsIntegrationsPage`
// (`/settings/integrations`). Ne plus l'importer ici.

const FIELDS = [
  ["name", "Nom de l'entreprise"],
  ["address", "Adresse"],
  ["phone", "Téléphone"],
  ["contact_email", "Email de contact"],
  ["website", "Site web"],
  ["bic", "BIC"],
];

export default function CompanySettingsPage() {
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, cgv: false });
  const [completeness, setCompleteness] = useState(null);

  const load = async () => {
    const [{ data }, { data: comp }] = await Promise.all([
      api.get("/company/settings"),
      api.get("/company/completeness").catch(() => ({ data: null })),
    ]);
    setCompany(data);
    setCompleteness(comp);
    setForm({
      name: data.name || "",
      slug: data.slug || "",
      accent_color: data.accent_color || "#C4553B",
      address: data.address || "",
      phone: data.phone || "",
      contact_email: data.contact_email || "",
      website: data.website || "",
      bic: data.bic || "",
      presentation_text: data.presentation_text || "",
      // Peppol Turn 2ter — Identité légale (source unique)
      vat_number: data.vat_number || "",
      street: data.street || "",
      street_number: data.street_number || "",
      zip_code: data.zip_code || "",
      city: data.city || "",
      country: data.country || "Belgium",
      // Phase 2C.C-ext (PDF v7) — Personnalisation PDF
      show_werko_footer: data.show_werko_footer !== false, // défaut true
      // Phase Rentab-1+2-B (13/09/2026) — coût interne main-d'œuvre
      daily_labor_cost:
        data.daily_labor_cost !== null && data.daily_labor_cost !== undefined
          ? String(data.daily_labor_cost)
          : "",
    });
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Phase Rentab-1+2-B — daily_labor_cost : string vide → null (unset), sinon float
      const payload = { ...form };
      if ("daily_labor_cost" in payload) {
        const raw = payload.daily_labor_cost;
        if (raw === "" || raw === null || raw === undefined) {
          payload.daily_labor_cost = null;
        } else {
          const n = Number(raw);
          if (!Number.isFinite(n) || n < 0) {
            toast.error("Coût journalier main-d'œuvre : nombre positif attendu.");
            setSaving(false);
            return;
          }
          payload.daily_labor_cost = n;
        }
      }
      const { data } = await api.patch("/company/settings", payload);
      setCompany(data);
      toast.success("Paramètres enregistrés");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/company/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(kind === "logo" ? "Logo mis à jour" : "CGV mis à jour");
      setCompany((c) => ({ ...c, ...data }));
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  if (!company) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  // Phase B2 — Scroll + focus sur la bannière "complétude" quand l'utilisateur
  // arrive via le badge sidebar (`?missing=1`). Utilise ref via DOM query pour
  // rester léger (pas de useRef) — l'élément a un data-testid stable.
  const highlightMissing = searchParams.get("missing") === "1";

  // Phase 10g — Résolution URL logo :
  //   - Si logo_file_id présent → endpoint public dédié /api/public/logos/{id} (cache long)
  //   - Sinon fallback logo_url legacy (peut être expiré si disque prod vaporisé)
  //   - Sinon null → afficher placeholder + message
  const logoFullUrl = company.logo_file_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/logos/${company.logo_file_id}`
    : company.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`
    : null;
  const cgvFullUrl = company.cgv_pdf_url
    ? `${process.env.REACT_APP_BACKEND_URL}${company.cgv_pdf_url}`
    : null;
  // C1A v2 (15/09/2026) — plus de lecture directe de localStorage :
  // on passe par getToken() qui lit sessionStorage (isolation par onglet).
  // `authToken` sert ici uniquement de cache-buster URL pour le logo legacy
  // (voir ligne src={...}?t=${authToken?.slice(-8)}).
  const authToken = getToken();

  return (
    <div className="space-y-6" data-testid="company-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Entreprise</h1>
        <p className="text-sm text-slate-400">
          Identité, coordonnées, branding et CGV. Ces infos apparaîtront dans les PDF devis et
          factures.
        </p>
      </div>

      {completeness && completeness.count_missing > 0 && (
        <div
          className={`flex items-start gap-3 rounded-lg border p-4 transition-shadow ${
            completeness.severity === "red"
              ? "border-error/60 bg-error-subtle/30"
              : "border-warning/60 bg-warning-subtle/30"
          } ${highlightMissing ? "shadow-[0_0_0_3px_hsl(var(--warning)/0.35)]" : ""}`}
          data-testid="settings-completeness-banner"
          ref={(el) => {
            if (el && highlightMissing) {
              // Scroll doux vers la bannière au premier render
              setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
            }
          }}
        >
          <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${
            completeness.severity === "red" ? "text-error-text" : "text-warning-text"
          }`} />
          <div className="min-w-0 flex-1 text-sm text-slate-200">
            <div className="mb-1 font-medium">
              {completeness.severity === "red"
                ? `${completeness.blocking_missing.length} paramètre(s) bloquant(s) à compléter pour émettre des factures conformes`
                : `${completeness.count_missing} paramètre(s) recommandé(s) à compléter`}
              <span className="ml-2 text-xs font-normal text-slate-400">
                Complétude : {completeness.score_pct}%
              </span>
            </div>
            <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
              {completeness.blocking_missing.map((f) => (
                <li key={f.field} className="text-error-text">
                  • <b>{f.label}</b> (bloquant)
                </li>
              ))}
              {completeness.recommended_missing.map((f) => (
                <li key={f.field}>• {f.label}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Peppol Turn 2ter — Identité légale (source unique VAT + adresse structurée) */}
      <Card className="border-slate-800 bg-slate-900/40" data-testid="legal-identity-card">
        <CardContent className="p-6">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-200">Identité légale</h2>
            <p className="mt-1 text-xs text-slate-400">
              Ces informations apparaissent sur vos factures, devis, et sont utilisées pour
              l&apos;e-facturation (Peppol).
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">
                Numéro TVA
                {/* Lot P0 #1 (2026-09-09) — Highlight rouge si vide ou mal formé.
                    La sauvegarde reste possible avec champ vide (défaut back). */}
                {!form.vat_number && (
                  <span
                    className="ml-2 text-xs font-normal text-[#F0C572]"
                    data-testid="vat-required-hint"
                  >
                    (requis pour émission facture)
                  </span>
                )}
                {form.vat_number && !/^BE\d{10}$/.test(form.vat_number) && (
                  <span
                    className="ml-2 text-xs font-normal text-red-400"
                    data-testid="vat-invalid-hint"
                  >
                    (format invalide)
                  </span>
                )}
              </Label>
              <Input
                value={form.vat_number || ""}
                onChange={(e) => setForm({
                  ...form,
                  vat_number: e.target.value.toUpperCase().replace(/[.\s]/g, ""),
                })}
                placeholder="BE0123456789"
                pattern="^BE\d{10}$"
                className={`bg-slate-950/40 text-slate-100 font-mono ${
                  form.vat_number && !/^BE\d{10}$/.test(form.vat_number)
                    ? "border-red-500/60"
                    : !form.vat_number
                    ? "border-[#F0C572]/40"
                    : "border-slate-700"
                }`}
                data-testid="company-vat-number-input"
              />
              <p className="text-[11px] text-slate-500">Format : BE + 10 chiffres, sans points ni espaces.</p>
            </div>

            <div className="space-y-1.5 sm:col-span-2 grid grid-cols-[3fr_1fr] gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Rue</Label>
                <Input
                  value={form.street || ""}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  placeholder="Rue de la Loi"
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="company-street-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Numéro</Label>
                <Input
                  value={form.street_number || ""}
                  onChange={(e) => setForm({ ...form, street_number: e.target.value })}
                  placeholder="1"
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="company-street-number-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300">Code postal</Label>
              <Input
                value={form.zip_code || ""}
                onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                placeholder="1000"
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="company-zip-code-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Ville</Label>
              <Input
                value={form.city || ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Bruxelles"
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="company-city-input"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">Pays</Label>
              <select
                value={form.country || "Belgium"}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100"
                data-testid="company-country-select"
              >
                <option value="Belgium">Belgium</option>
                <option value="France">France</option>
                <option value="Netherlands">Netherlands</option>
                <option value="Luxembourg">Luxembourg</option>
                <option value="Germany">Germany</option>
              </select>
            </div>
          </div>

          {form.address && (
            <div className="mt-6 rounded-md border border-slate-800 bg-slate-950/40 p-3" data-testid="legacy-address-block">
              <div className="mb-1 text-xs font-medium text-slate-400">
                Adresse legacy (concaténée) — à supprimer une fois l&apos;adresse structurée remplie
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 truncate font-mono text-xs text-slate-300">{form.address}</div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm({ ...form, address: "" })}
                  data-testid="clear-legacy-address-btn"
                >
                  Effacer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-slate-300">{label}</Label>
              <Input
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid={`company-${key}-input`}
              />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-slate-300">Identifiant public (slug)</Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">werko.app/</span>
              <Input
                value={form.slug || ""}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                placeholder="mon-entreprise"
                className="flex-1 border-slate-700 bg-slate-950/40 text-slate-100 font-mono"
                data-testid="company-slug-input"
                pattern="^[a-z0-9](-?[a-z0-9])*$"
              />
            </div>
            <p className="text-[11px] text-slate-500">
              Utilisé dans les URLs de l&apos;espace client et de la documentation webhook. 3-40 caractères,
              minuscules, chiffres et tirets uniquement. Non modifiable si des tokens webhook actifs ou
              des devis envoyés en dépendent.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Couleur d'accent (PDF & espace client)</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.accent_color || "#C4553B"}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="h-10 w-14 cursor-pointer rounded border border-slate-700 bg-transparent"
                data-testid="company-accent-color"
              />
              <span className="rounded px-2 py-1 text-xs font-mono text-slate-300">
                {form.accent_color}
              </span>
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                style={{ background: form.accent_color }}
              >
                Aperçu badge
              </span>
              <span
                className="rounded-full bg-white px-3 py-1 text-xs font-semibold"
                style={{ color: form.accent_color }}
              >
                Aperçu sur blanc
              </span>
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-slate-300">Présentation (utilisée par l&apos;IA pour rédiger vos communications dans votre ton)</Label>
            <Textarea
              rows={4}
              value={form.presentation_text || ""}
              onChange={(e) => setForm({ ...form, presentation_text: e.target.value })}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="company-presentation-input"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Logo */}
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
              <ImageIcon className="h-4 w-4" /> Logo (PNG/JPG, max 2 Mo)
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div
                className="flex h-24 w-24 items-center justify-center rounded-lg border border-dashed border-slate-700 bg-slate-950/50"
                data-testid="logo-preview"
              >
                {logoFullUrl ? (
                  <img
                    src={company.logo_file_id ? logoFullUrl : `${logoFullUrl}?t=${authToken?.slice(-8) || ""}`}
                    alt="Logo"
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                    data-testid="logo-preview-img"
                  />
                ) : (
                  <span className="text-xs text-slate-500 px-2 text-center" data-testid="logo-placeholder">
                    Aucun logo. Ajoutes-en un via le bouton ci-dessous.
                  </span>
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => upload("logo", e.target.files?.[0])}
                  data-testid="logo-file-input"
                />
                <span className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">
                  {uploading.logo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {logoFullUrl ? "Remplacer" : "Uploader"}
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* CGV */}
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
              <FileText className="h-4 w-4" /> CGV (PDF, max 5 Mo)
            </div>
            <div className="mt-4 flex items-center gap-4">
              {cgvFullUrl && (
                <a
                  href={cgvFullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border border-slate-700 bg-slate-950/50 px-3 py-1.5 text-xs text-[#C4553B] hover:underline"
                  data-testid="cgv-preview-link"
                >
                  Ouvrir le PDF actuel
                </a>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => upload("cgv", e.target.files?.[0])}
                  data-testid="cgv-file-input"
                />
                <span className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">
                  {uploading.cgv ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {cgvFullUrl ? "Remplacer" : "Uploader"}
                </span>
              </label>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase Rentab-1+2-B (13/09/2026) — Coûts internes chantier (admin only) */}
      <Card
        className="border-slate-800 bg-slate-900/40"
        data-testid="internal-costs-card"
      >
        <CardContent className="p-6">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-200">Coûts internes chantier</h2>
            <p className="mt-1 text-xs text-slate-400">
              Utilisé par le calcul de marge sur achats pour valoriser la main-d&apos;œuvre.
              Renseigne un coût journalier chargé moyen (salaire brut + charges patronales + frais indirects)
              par ouvrier salarié. Laisse vide si tu ne veux pas déduire la main-d&apos;œuvre de la marge.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">
                Coût journalier main-d&apos;œuvre (€ HTVA)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={form.daily_labor_cost ?? ""}
                onChange={(e) =>
                  setForm({ ...form, daily_labor_cost: e.target.value })
                }
                placeholder="Ex : 320"
                className="border-slate-700 bg-slate-950/40 text-slate-100 font-mono tabular-nums"
                data-testid="input-daily-labor-cost"
              />
              <p className="text-[11px] text-slate-500">
                Salaire brut + charges patronales + frais indirects (véhicule,
                assurances, outillage). Nombre positif, sans TVA.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2C.C-ext (PDF v7) — Personnalisation PDF */}
      <Card className="border-slate-800 bg-slate-900/40" data-testid="pdf-customization-card">
        <CardContent className="p-6">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-200">Personnalisation PDF</h2>
            <p className="mt-1 text-xs text-slate-400">
              Contrôle l&apos;apparence des documents PDF générés (devis, factures).
            </p>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-800 bg-slate-950/40 p-3 hover:border-slate-700">
            <input
              type="checkbox"
              checked={form.show_werko_footer !== false}
              onChange={(e) =>
                setForm({ ...form, show_werko_footer: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-950 text-[#C4553B] focus:ring-[#C4553B]"
              data-testid="show-werko-footer-checkbox"
            />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium text-slate-200">
                Afficher &laquo;&nbsp;Document généré via Wérko&nbsp;&raquo; en pied de page
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Décoché : le pied de page discret en bas à droite des PDF devis et
                factures est masqué. Le reste du footer (nom société, TVA, numéro
                de document, pagination) reste inchangé.
              </p>
            </div>
          </label>
        </CardContent>
      </Card>

      <Button
        onClick={save}
        disabled={saving}
        className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
        data-testid="company-save-btn"
      >
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Enregistrer
      </Button>
    </div>
  );
}
